import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PlusIcon } from '../../../../../assets/svg/PlusIcon';
import ArrowBackIcon from '../../../../../assets/svg/route-actions-menu/ArrowBackIcon';
import CopyStopsIcon from '../../../../../assets/svg/route-actions-menu/CopyStopsIcon';
import DeleteStopsIcon from '../../../../../assets/svg/route-actions-menu/DeleteStopsIcon';
import FinishRouteIcon from '../../../../../assets/svg/route-actions-menu/FinishRouteIcon';
import ReorderRouteIcon from '../../../../../assets/svg/route-actions-menu/ReorderRouteIcon';
import { FastActionsMenu } from '../../../../../components/FastActionsMenu';
import { Modal } from '../../../../../components/Modal';
import { StopsManagementMenu } from '../../../../../components/StopsManagementMenu/StopsManagementMenu';
import { ROUTE_STATUS } from '../../../../../constants/Routes';
import * as routing from '../../../../../constants/Routing';
import { STOP_STATUS } from '../../../../../constants/Stops';
import { Route } from '../../../../../domain/route/Route';
import Button from '../../../../../react-components/Button';
import { usePermissions } from '../../../hooks/usePermissions';
import { useRetrieveVehicle } from '../../../hooks/useRetrieveVehicle';
import { PRIVATE_COMPONENT_ID } from '../../../PrivateComponent';
import { RouteDragIcon } from '../RouteDragIcon';
import { Finished } from './Finished';
import { InTransit } from './InTransit';
import { NotStarted } from './NotStarted';

type RouteStopsContainerHeaderProps = {
  selectedRoute: Route;
  onEndRoute: () => void;
  openSearch: () => void;
};

export const RouteStopsContainerHeader = ({
  selectedRoute,
  onEndRoute,
  openSearch,
}: RouteStopsContainerHeaderProps) => {
  const navigate = useNavigate();
  const { data: vehicleData } = useRetrieveVehicle();
  const { canManageStops, canReorderStops } = usePermissions();
  const [endRouteDialogOpen, setEndRouteDialogOpen] = useState<boolean>(false);
  const [isViewingCopyStops, setIsViewingCopyStops] = useState<boolean>(false);
  const [isViewingDeleteStops, setIsViewingDeleteStops] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState(false);

  if (!vehicleData) {
    return null;
  }

  const contentStyles: React.CSSProperties = {
    padding: `0 20px`,
  };
  const buttonMenuStyles: React.CSSProperties = {};
  let content: JSX.Element | null = null;

  switch (selectedRoute.status) {
    case ROUTE_STATUS.notStarted:
      content = <NotStarted selectedRoute={selectedRoute} />;
      contentStyles[`marginBottom`] = `12px`;
      break;
    case ROUTE_STATUS.inTransit:
      content = <InTransit selectedRoute={selectedRoute} />;
      contentStyles[`position`] = `relative`;
      contentStyles[`padding`] = `0`;
      buttonMenuStyles[`marginRight`] = `20px`;
      break;
    case ROUTE_STATUS.finished:
      contentStyles[`position`] = `relative`;
      contentStyles[`padding`] = `0`;
      buttonMenuStyles[`marginRight`] = `20px`;
      content = <Finished selectedRoute={selectedRoute} />;
      break;
  }

  const fastActionsToBeShown = {
    reorderRoute:
      canReorderStops &&
      selectedRoute.stops.length > 0 &&
      selectedRoute.status !== `finished` &&
      selectedRoute.getNumberPendingStops() > 0,
    addStops: canManageStops && selectedRoute.status !== `finished`,
    copyStops: canManageStops && selectedRoute.status !== `finished`,
    deleteStops: canManageStops && selectedRoute.status !== `finished` && selectedRoute.stops.length > 0,
    endRoute: selectedRoute.status === `in_transit`,
  };

  return (
    <>
      <Grid container direction="column" alignItems="center">
        <RouteDragIcon />
        <Grid container direction="row" wrap="nowrap" style={contentStyles}>
          <div style={{ width: `100%` }}>{content}</div>
          <Button
            style={{
              margin: `0`,
              padding: `0`,
              border: `none`,
              width: `32px`,
              height: `30px`,
              borderRadius: `50%`,
              backgroundColor: `transparent`,
              ...buttonMenuStyles,
            }}
            onClick={() => setMenuOpen(true)}
          >
            <MoreVertIcon style={{ color: RoutalPalette.primary80 }} />
          </Button>
        </Grid>
      </Grid>

      <FastActionsMenu
        openMenu={menuOpen}
        setOpenMenu={setMenuOpen}
        portalElementId={PRIVATE_COMPONENT_ID}
        actions={[
          ...(fastActionsToBeShown.addStops
            ? [
                {
                  intlId: `drivers.route_header.actions_menu.add_stops`,
                  icon: <PlusIcon color={RoutalPalette.primary40} />,
                  onClick: () => {
                    openSearch();
                    setMenuOpen(false);
                  },
                },
              ]
            : []),
          ...(fastActionsToBeShown.reorderRoute
            ? [
                {
                  intlId: `drivers.route_header.actions_menu.reorder_route`,
                  icon: <ReorderRouteIcon />,
                  onClick: () => {
                    navigate(routing.PRIVATE_ROUTES.ROUTE_REORDER.replace(`:routeId`, selectedRoute.id));
                    setMenuOpen(false);
                  },
                },
              ]
            : []),
          ...(fastActionsToBeShown.copyStops
            ? [
                {
                  intlId: `drivers.route_header.actions_menu.copy_stops`,
                  icon: <CopyStopsIcon />,
                  onClick: () => {
                    setIsViewingCopyStops(true);
                    setMenuOpen(false);
                  },
                },
              ]
            : []),
          ...(fastActionsToBeShown.endRoute
            ? [
                {
                  intlId: `drivers.route_header.actions_menu.end_route`,
                  icon: <FinishRouteIcon />,
                  onClick: () => {
                    if (selectedRoute.getNumberPendingStops() > 0) {
                      setEndRouteDialogOpen(true);
                      return;
                    }
                    onEndRoute();
                  },
                },
              ]
            : []),
          ...(fastActionsToBeShown.deleteStops
            ? [
                {
                  intlId: `drivers.route_header.actions_menu.delete_stops`,
                  textStyles: { color: RoutalPalette.error.medium },
                  icon: <DeleteStopsIcon />,
                  onClick: () => {
                    setIsViewingDeleteStops(true);
                    setMenuOpen(false);
                  },
                },
              ]
            : []),
          null,
          {
            intlId: `drivers.route_header.actions_menu.back_to_routes`,
            icon: <ArrowBackIcon />,
            onClick: () => {
              navigate(routing.PRIVATE_ROUTES.ROOT);
            },
          },
        ]}
      />

      <StopsManagementMenu
        isOpen={isViewingCopyStops}
        selectedRoute={selectedRoute}
        onClose={() => setIsViewingCopyStops(false)}
        mode="copy"
        portalElementId={PRIVATE_COMPONENT_ID}
      />

      <StopsManagementMenu
        isOpen={isViewingDeleteStops}
        selectedRoute={selectedRoute}
        onClose={() => {
          setIsViewingDeleteStops(false);
        }}
        mode="delete"
        portalElementId={PRIVATE_COMPONENT_ID}
      />

      <Modal
        open={endRouteDialogOpen}
        portalElementId={PRIVATE_COMPONENT_ID}
        title={{
          intlId: `driver.pending_stops_warning.title`,
        }}
        subtitle={{
          intlId: `driver.pending_stops_warning.info`,
          intlValues: {
            pendingServices: selectedRoute.stops.filter((s) => s.status === STOP_STATUS.pending).length,
          },
        }}
        buttons={[
          {
            intlId: `confirm`,
            onClick: () => {
              onEndRoute();
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setEndRouteDialogOpen(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />
    </>
  );
};
